/**
 * Query-cache persistence, so the book of business is readable without a connection.
 *
 * The important part is what is *not* persisted. Writing the whole cache to disk would
 * put the audit trail, user accounts and commission statements into IndexedDB, where
 * they outlive the session on a shared or stolen machine. `dehydrateOptions` therefore
 * filters against the allow-list in `@ias/shared/offline`, and the store is wiped on
 * sign-out — cached client data must not survive the person who was signed in.
 */

import type { PersistedClient, Persister } from '@tanstack/react-query-persist-client';
import { isCacheableQueryRoot } from '@ias/shared';

import { idb, STORE_CACHE } from './idb';

const CACHE_KEY = 'query-cache';

/** Bump to invalidate every persisted cache after a shape change. */
export const CACHE_BUSTER = 'v1';

export function createIdbPersister(): Persister {
  return {
    persistClient: async (client: PersistedClient) => {
      await idb.put(STORE_CACHE, client, CACHE_KEY);
    },
    restoreClient: async () => idb.get<PersistedClient>(STORE_CACHE, CACHE_KEY),
    removeClient: async () => {
      await idb.delete(STORE_CACHE, CACHE_KEY);
    },
  };
}

/** Drops every persisted read. Called on sign-out and on a failed session refresh. */
export async function purgePersistedCache(): Promise<void> {
  await idb.clear(STORE_CACHE);
}

/**
 * Decides, per query, whether it may be written to disk.
 *
 * Only successful queries whose key begins with an allow-listed root qualify. Anything
 * else — errors, and every sensitive root — is held in memory for the session and
 * forgotten on reload, which is the safe default.
 */
export const dehydrateOptions = {
  shouldDehydrateQuery: (query: { queryKey: readonly unknown[]; state: { status: string } }) =>
    query.state.status === 'success' && isCacheableQueryRoot(query.queryKey[0]),
  // Mutations are never persisted; queued writes live in the outbox, which has its own
  // ordering, retry and idempotency guarantees.
  shouldDehydrateMutation: () => false,
};

/** How long a persisted cache stays usable before it is discarded as too stale to trust. */
export const MAX_CACHE_AGE_MS = 7 * 24 * 60 * 60 * 1000;
